import { useState, useEffect, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Key, MessageCircle, Copy, Check, Menu, X, Loader2, Lock, Unlock, LayoutDashboard } from 'lucide-react';
import { toast } from 'sonner';

// Types for API requests
type ServerMode = 'mock' | 'live';

interface ApiResponse {
  status: number;
  data: unknown;
  time: number;
}

// Sidebar navigation item
function NavItem({ href, children, active = false, onClick }: { href: string; children: React.ReactNode; active?: boolean; onClick?: () => void }) {
  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    onClick?.();
  };

  return (
    <button
      onClick={handleClick}
      className={`w-full text-left flex items-center gap-2 px-4 py-2 text-sm transition-colors ${
        active
          ? 'text-blue-400 border-l-2 border-blue-400 bg-blue-400/10'
          : 'text-gray-400 hover:text-white hover:bg-white/5'
      }`}
    >
      {children}
    </button>
  );
}

// Method badge
function MethodBadge({ method }: { method: 'GET' | 'POST' }) {
  const colors = {
    GET: 'bg-green-500/20 text-green-400 border-green-500/30',
    POST: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  };
  return (
    <span className={`text-xs font-mono font-bold px-2 py-0.5 rounded border ${colors[method]}`}>
      {method}
    </span>
  );
}

// Status badge
function StatusBadge({ code }: { code: number }) {
  const colors: Record<number, string> = {
    200: 'bg-green-500/20 text-green-400',
    400: 'bg-yellow-500/20 text-yellow-400',
    500: 'bg-red-500/20 text-red-400',
    503: 'bg-orange-500/20 text-orange-400',
  };
  return (
    <span className={`inline-flex text-xs font-mono font-bold px-2 py-1 rounded mb-2 ${colors[code] || 'bg-gray-500/20 text-gray-400'}`}>
      {code}
    </span>
  );
}

// Code block
function CodeBlock({ lang, children }: { lang: string; children: React.ReactNode }) {
  const [copied, setCopied] = useState(false);
  
  const handleCopy = () => {
    const text = typeof children === 'string' ? children : '';
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success('Скопировано!');
  };

  return (
    <div className="bg-[#070c18] border border-[#1a2340] rounded-lg overflow-hidden my-4">
      <div className="flex items-center justify-between px-3 py-2 border-b border-[#1a2340] bg-white/[0.02]">
        <span className="text-xs text-gray-500 font-mono uppercase">{lang}</span>
        <button
          onClick={handleCopy}
          className="text-xs text-gray-500 hover:text-white transition-colors px-2 py-1 rounded hover:bg-white/5"
        >
          {copied ? 'Copied!' : 'Copy'}
        </button>
      </div>
      <pre className="p-3 overflow-x-auto font-mono text-sm leading-relaxed text-gray-300">
        {children}
      </pre>
    </div>
  );
}

// Parameter row
function ParamRow({ name, type, required, desc }: { name: string; type: string; required?: boolean; desc: React.ReactNode }) {
  return (
    <div className="flex flex-col sm:flex-row gap-1 sm:gap-4 py-3 border-b border-[#252d42]/50 last:border-0">
      <div className="w-full sm:w-40 shrink-0">
        <code className="text-blue-400 text-sm font-semibold">{name}</code>
        <div className="flex gap-2 mt-1">
          <code className="text-purple-400 text-xs">{type}</code>
          {required ? (
            <span className="text-red-400 text-xs font-bold uppercase">required</span>
          ) : (
            <span className="text-gray-500 text-xs uppercase">optional</span>
          )}
        </div>
      </div>
      <div className="flex-1 text-sm text-gray-400">{desc}</div>
    </div>
  );
}

// Section wrapper
function Section({ id, children }: { id: string; children: React.ReactNode }) {
  return (
    <div id={id} className="scroll-mt-5">
      {children}
    </div>
  );
}

// Two column layout
function TwoCol({ left, right }: { left: React.ReactNode; right: React.ReactNode }) {
  return (
    // На узком экране колонки складываются: панель Try it уезжает под
    // документацию, а не сжимает её до нечитаемой ширины. Фиксированные
    // 460px и вертикальная граница включаются только с lg.
    <div className="flex flex-col lg:flex-row border-b border-[#252d42] min-h-0">
      <div className="flex-1 min-w-0 p-4 sm:p-6 lg:p-8 lg:border-r border-[#252d42]">
        {left}
      </div>
      {right && (
        <div className="w-full lg:w-[460px] lg:min-w-[460px] bg-[#0d1220] flex flex-col border-t lg:border-t-0 border-[#252d42]">
          {right}
        </div>
      )}
    </div>
  );
}

// TryIt interactive panel component
function TryItPanel({ 
  title, 
  children,
  serverMode,
  onServerModeChange,
  showModeToggle = true
}: { 
  title: string; 
  children?: React.ReactNode;
  serverMode?: ServerMode;
  onServerModeChange?: (mode: ServerMode) => void;
  showModeToggle?: boolean;
}) {
  return (
    <div className="h-full flex flex-col text-sm min-h-[420px]">
      <div className="flex items-center justify-between px-4 h-10 bg-[#090e1a] border-b border-[#1a2340]">
        <span className="text-xs font-bold uppercase tracking-wider text-gray-600">{title}</span>
        {showModeToggle && onServerModeChange && serverMode && (
          <div className="flex bg-[#0f1626] rounded overflow-hidden border border-[#1e2a45]">
            <button 
              onClick={() => onServerModeChange('mock')}
              className={`px-3 py-1 text-xs flex items-center gap-1 transition-colors ${
                serverMode === 'mock' 
                  ? 'bg-purple-500/10 text-purple-400' 
                  : 'text-gray-500 hover:text-gray-300'
              }`}
            >
              <span className="w-1 h-1 rounded-full bg-current" />
              Mock
            </button>
            <button 
              onClick={() => onServerModeChange('live')}
              className={`px-3 py-1 text-xs flex items-center gap-1 transition-colors ${
                serverMode === 'live' 
                  ? 'bg-green-500/10 text-green-400' 
                  : 'text-gray-500 hover:text-gray-300'
              }`}
            >
              <span className="w-1 h-1 rounded-full bg-current" />
              Live
            </button>
          </div>
        )}
      </div>
      {children}
    </div>
  );
}

// Auth bar component
function AuthBar({
  token,
  onTokenChange,
  serverMode,
  savedApiKey,
}: {
  token: string;
  onTokenChange: (token: string) => void;
  serverMode: ServerMode;
  savedApiKey?: string;
}) {
  return (
    <div className="flex items-center gap-0 border-b border-[#1a2340] h-9 bg-[#0a0f1c]">
      <span className="text-[10px] font-bold uppercase text-gray-600 px-3 h-full flex items-center bg-[#090e1a] border-r border-[#1a2340]">
        Auth
      </span>
      {serverMode === 'mock' ? (
        <div className="flex items-center gap-2 px-3 text-xs text-gray-500">
          <span>🔷</span>
          <span>Mock mode — токен не нужен</span>
        </div>
      ) : (
        <div className="flex-1 flex items-center px-3 gap-2">
          {savedApiKey ? (
            <span className="flex items-center gap-1 text-[9px] font-bold uppercase text-green-500">
              <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
              Authorized
            </span>
          ) : (
            <span className="text-[9px] font-bold uppercase text-gray-600">Token</span>
          )}
          <input
            type="password"
            value={token}
            onChange={(e) => onTokenChange(e.target.value)}
            placeholder="Вставьте API ключ..."
            className="flex-1 bg-transparent border-none text-xs text-white outline-none font-mono"
          />
        </div>
      )}
    </div>
  );
}

// JSON syntax highlighter
/** Безопасная подсветка JSON через JSX (React сам экранирует текст —
 *  никакого dangerouslySetInnerHTML, никаких regex с HTML-инъекцией). */
function HighlightedJSON({ value }: { value: unknown }) {
  const text = JSON.stringify(value, null, 2);
  // Регулярка с альтернативами:
  //   1) JSON-строка с поддержкой escape \"
  //   2) литералы true/false/null
  //   3) число (в т.ч. экспоненциальная форма)
  //   4) всё остальное (скобки, пробелы, запятые, двоеточия) — без подсветки
  const tokenRe = /("(?:\\.|[^"\\])*")|\b(true|false|null)\b|(-?\d+(?:\.\d+)?(?:[eE][-+]?\d+)?)/g;

  const nodes: ReactNode[] = [];
  let lastIdx = 0;
  let match: RegExpExecArray | null;
  let key = 0;

  while ((match = tokenRe.exec(text)) !== null) {
    if (match.index > lastIdx) {
      nodes.push(text.slice(lastIdx, match.index));
    }
    const [whole, str, lit, num] = match;
    if (str) {
      // Строка-ключ, если за ней (после возможных пробелов) идёт ':'
      const after = text.slice(match.index + str.length);
      const isKey = /^\s*:/.test(after);
      nodes.push(
        <span key={key++} className={isKey ? 'text-blue-400' : 'text-green-400'}>
          {str}
        </span>
      );
    } else if (lit) {
      nodes.push(
        <span key={key++} className="text-purple-400">{lit}</span>
      );
    } else if (num) {
      nodes.push(
        <span key={key++} className="text-yellow-400">{num}</span>
      );
    } else {
      nodes.push(whole);
    }
    lastIdx = match.index + whole.length;
  }
  if (lastIdx < text.length) {
    nodes.push(text.slice(lastIdx));
  }
  return <>{nodes}</>;
}

// Response display component
function ResponseDisplay({ 
  response, 
  loading, 
  onShowMockStatus,
  activeMockStatus = 200
}: { 
  response: ApiResponse | null; 
  loading: boolean;
  onShowMockStatus?: (code: number) => void;
  activeMockStatus?: number;
}) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    if (response) {
      navigator.clipboard.writeText(JSON.stringify(response.data, null, 2));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success('Скопировано!');
    }
  };

  const getStatusColor = (status: number, active: boolean) => {
    const baseClasses = 'text-[10px] font-mono font-bold px-2 py-1 rounded cursor-pointer transition-all border';
    if (status === 200) {
      return active 
        ? `${baseClasses} bg-green-500/20 text-green-400 border-green-500/40 shadow-[0_0_0_1px_rgba(74,222,128,0.5)]` 
        : `${baseClasses} bg-green-500/10 text-green-400/70 border-green-500/20 hover:bg-green-500/20`;
    }
    if (status === 400) {
      return active 
        ? `${baseClasses} bg-yellow-500/20 text-yellow-400 border-yellow-500/40 shadow-[0_0_0_1px_rgba(250,204,21,0.5)]` 
        : `${baseClasses} bg-yellow-500/10 text-yellow-400/70 border-yellow-500/20 hover:bg-yellow-500/20`;
    }
    if (status === 500) {
      return active 
        ? `${baseClasses} bg-red-500/20 text-red-400 border-red-500/40 shadow-[0_0_0_1px_rgba(248,113,113,0.5)]` 
        : `${baseClasses} bg-red-500/10 text-red-400/70 border-red-500/20 hover:bg-red-500/20`;
    }
    return active 
      ? `${baseClasses} bg-orange-500/20 text-orange-400 border-orange-500/40 shadow-[0_0_0_1px_rgba(251,146,60,0.5)]` 
      : `${baseClasses} bg-orange-500/10 text-orange-400/70 border-orange-500/20 hover:bg-orange-500/20`;
  };

  return (
    <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
      <div className="flex items-center px-4 py-2 bg-[#090e1a] border-b border-[#1a2340] gap-2">
        <span className="text-[10px] font-bold uppercase tracking-wider text-gray-600">Response</span>
        <div className="flex gap-1 ml-2">
          {[200, 400, 500, 503].map((code) => (
            <button
              key={code}
              onClick={() => onShowMockStatus?.(code)}
              className={getStatusColor(code, activeMockStatus === code)}
            >
              {code}
            </button>
          ))}
        </div>
        {response && (
          <button
            onClick={handleCopy}
            className="text-[10px] text-gray-500 hover:text-white ml-auto px-2 py-0.5 rounded hover:bg-white/5"
          >
            {copied ? 'Copied!' : 'Copy'}
          </button>
        )}
      </div>
      <div className="flex-1 overflow-auto p-4 font-mono text-xs leading-relaxed">
        {loading ? (
          <div className="flex items-center justify-center h-full text-gray-500">
            <Loader2 className="w-4 h-4 animate-spin mr-2" />
            Отправка запроса...
          </div>
        ) : response ? (
          response.data != null ? (
            <pre className="text-gray-300 whitespace-pre font-mono text-xs leading-relaxed m-0">
              <HighlightedJSON value={response.data} />
            </pre>
          ) : (
            <span className="text-gray-600 italic">// Нет данных</span>
          )
        ) : (
          <span className="text-gray-600 italic">// Нажмите Send Request для выполнения запроса</span>
        )}
      </div>
    </div>
  );
}

export function B2BApiPage() {
const [showKeyDialog, setShowKeyDialog] = useState(false);
  const [showAuthorizeDialog, setShowAuthorizeDialog] = useState(false);
  const [authorizeInput, setAuthorizeInput] = useState('');
  const [savedApiKey, setSavedApiKey] = useState<string>(
    () => localStorage.getItem('xpanda_api_key') ?? ''
  );
  const [copied, setCopied] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('overview');

  // Server mode state for each endpoint
  const [serverModes, setServerModes] = useState<Record<string, ServerMode>>({
    ping: 'mock',
    balance: 'mock',
    prices: 'mock',
    fullPrices: 'mock',
    purchasesGet: 'mock',
    purchasesPost: 'mock',
  });

  // Token state
  const [tokens, setTokens] = useState<Record<string, string>>({
    ping: '',
    balance: '',
    prices: '',
    fullPrices: '',
    purchasesGet: '',
    purchasesPost: '',
  });

  // Response states
  const [responses, setResponses] = useState<Record<string, ApiResponse | null>>({
    ping: null,
    balance: null,
    prices: null,
    fullPrices: null,
    purchasesGet: null,
    purchasesPost: null,
  });

  // Loading states
  const [loading, setLoading] = useState<Record<string, boolean>>({
    ping: false,
    balance: false,
    prices: false,
    fullPrices: false,
    purchasesGet: false,
    purchasesPost: false,
  });

  // Form data states
  const [pricesNamesList, setPricesNamesList] = useState<string[]>(['']);
  const [purchasesIdsList, setPurchasesIdsList] = useState<string[]>(['']);
  const [purchasesCustomIdsList, setPurchasesCustomIdsList] = useState<string[]>(['']);
  const [postPurchaseData, setPostPurchaseData] = useState({
    product: '',
    partner: '',
    token: '',
    max_price: '',
    custom_id: '',
    sign: '',
  });

  // Active mock status for each endpoint (for showing different HTTP code examples)
  const [activeMockStatuses, setActiveMockStatuses] = useState<Record<string, number>>({
    ping: 200,
    balance: 200,
    prices: 200,
    fullPrices: 200,
    purchasesGet: 200,
    purchasesPost: 200,
  });

  const setServerMode = (endpoint: string, mode: ServerMode) => {
    setServerModes(prev => ({ ...prev, [endpoint]: mode }));
  };

  // Sync global saved key → all endpoint tokens
  useEffect(() => {
    setTokens({
      ping: savedApiKey,
      balance: savedApiKey,
      prices: savedApiKey,
      fullPrices: savedApiKey,
      purchasesGet: savedApiKey,
      purchasesPost: savedApiKey,
    });
  }, [savedApiKey]);

  const saveApiKey = (key: string) => {
    const trimmed = key.trim();
    localStorage.setItem('xpanda_api_key', trimmed);
    setSavedApiKey(trimmed);
    setShowAuthorizeDialog(false);
    toast.success('API ключ сохранён');
  };

  const clearApiKey = () => {
    localStorage.removeItem('xpanda_api_key');
    setSavedApiKey('');
    setAuthorizeInput('');
    setShowAuthorizeDialog(false);
    toast.success('API ключ удалён');
  };

  const showMockStatus = (endpoint: string, code: number) => {
    setActiveMockStatuses(prev => ({ ...prev, [endpoint]: code }));
    
    // Set mock response based on code
    let mockData: unknown;
    const startTime = Date.now();
    
    switch (code) {
      case 400:
        mockData = null;
        break;
      case 500:
        mockData = null;
        break;
      case 503:
        mockData = null;
        break;
      default:
        // For 200, generate appropriate success response
        switch (endpoint) {
          case 'ping':
            mockData = {
              status: 'ok',
              timestamp: Date.now(),
            };
            break;
          case 'balance':
            mockData = { total: 5000000, locked: 1200000, available: 3800000 };
            break;
          case 'prices':
            mockData = {
              timestamp: Date.now(),
              items: [
                { n: 'AK-47 | Redline (Field-Tested)', p: 15230, q: 45 },
                { n: 'M4A4 | Howl (Factory New)', p: 4500000, q: 2 },
              ]
            };
            break;
          case 'fullPrices':
            mockData = {
              download_url:
                'https://static.p2p.xpanda.pro/prices/price_1785247813.json'
                + '?X-Amz-Algorithm=AWS4-HMAC-SHA256&X-Amz-Expires=300&X-Amz-Signature=...',
              expires_in_seconds: 300,
            };
            break;
          case 'purchasesGet':
            mockData = {
              id: '019fc4e9-3ef8-786a-baf3-2001fd634350',
              custom_id: '42298052',
              product: 'M4A1-S | Emphorosaur-S (Field-Tested)',
              status: 'Error',
              offer_id: '',
              max_price: 484,
              partner: '123456789',
              token: 'aBcD1234',
              created_at: 1785715048,
              updated_at: 1785715049,
              active_order: {
                status: 'Error',
                exec_price: 419,
                steam_trade_offer_id: null,
                steam_trade_offer_expires_at: null,
                steam_trade_settle_at: null,
              },
              error_code: 'private_inventory',
            };
            break;
          case 'purchasesPost':
            mockData = {
              id: '550e8400-e29b-41d4-a716-446655440001',
              custom_id: postPurchaseData.custom_id || 'new_order',
              status: 'MONEY_BLOCKED',
              product: postPurchaseData.product || 'Test Item',
              max_price: parseInt(postPurchaseData.max_price) || 10000,
              active_order: {
                id: 'ord-uuid-new',
                status: 'PENDING',
                exec_price: null,
                error_code: null
              }
            };
            break;
          default:
            mockData = { message: 'Success' };
        }
    }

    setResponses(prev => ({
      ...prev,
      [endpoint]: {
        status: code,
        data: mockData,
        time: Date.now() - startTime
      }
    }));
  };

  const sendRequest = async (endpoint: string, method: 'GET' | 'POST', path: string, body?: unknown) => {
    const token = tokens[endpoint];
    const serverMode = serverModes[endpoint];

    setLoading(prev => ({ ...prev, [endpoint]: true }));
    const startTime = Date.now();

    try {
      if (serverMode === 'mock') {
        // Use the active mock status to show different responses
        await new Promise(r => setTimeout(r, 500));
        showMockStatus(endpoint, activeMockStatuses[endpoint] || 200);
        setLoading(prev => ({ ...prev, [endpoint]: false }));
        return;
      } else {
        // Live requests — via backend proxy to avoid CORS
        const PROXY_URL = `${import.meta.env.VITE_API_URL || '/api/v1'}/b2b-proxy`;
        const accessToken = localStorage.getItem('access_token');

        // Build query params as array of [key, value] pairs (supports repeated keys)
        const params: [string, string][] = [];
        if (method === 'GET') {
          if (endpoint === 'prices') {
            pricesNamesList.map(n => n.trim()).filter(Boolean).forEach(n => params.push(['names[]', n]));
          }
          if (endpoint === 'purchasesGet') {
            purchasesIdsList.map(n => n.trim()).filter(Boolean).forEach(n => params.push(['id', n]));
            purchasesCustomIdsList.map(n => n.trim()).filter(Boolean).forEach(n => params.push(['custom_id', n]));
          }
        }

        const response = await fetch(PROXY_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(accessToken ? { 'Authorization': `Bearer ${accessToken}` } : {}),
          },
          body: JSON.stringify({ method, path, token, params, body }),
        });

        const result = await response.json().catch(() => ({}));

        setResponses(prev => ({
          ...prev,
          [endpoint]: {
            status: result.status ?? response.status,
            data: result.data ?? result,
            time: Date.now() - startTime,
          }
        }));
      }
    } catch (error) {
      setResponses(prev => ({
        ...prev,
        [endpoint]: {
          status: 0,
          data: { error: 'Network error', message: String(error) },
          time: Date.now() - startTime
        }
      }));
    } finally {
      setLoading(prev => ({ ...prev, [endpoint]: false }));
    }
  };

  // Track active section on scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      { rootMargin: '-20% 0px -80% 0px', threshold: 0 }
    );

    const sections = document.querySelectorAll('div[id]');
    sections.forEach((section) => observer.observe(section));

    return () => observer.disconnect();
  }, []);


  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success('Скопировано!');
  };

  const SidebarContent = ({ onItemClick }: { onItemClick?: () => void } = {}) => (
    <>
      {/* Logo */}
      <div className="flex items-center gap-3 px-4 py-5 border-b border-[#252d42]">
        <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center font-black text-white text-sm shadow-lg shadow-blue-500/30">
          P
        </div>
        <div>
          <div className="font-bold text-sm">xPanda P2P</div>
          <div className="text-xs text-gray-500">API Docs · v1</div>
        </div>
      </div>

      {/* Navigation */}
      <div className="flex-1 py-2 overflow-y-auto">
        <div className="px-4 py-2 text-xs font-bold uppercase tracking-wider text-gray-600">
          Начало работы
        </div>
        <NavItem href="#overview" active={activeSection === 'overview'} onClick={onItemClick}>📖 Обзор</NavItem>
        <NavItem href="#authentication" active={activeSection === 'authentication'} onClick={onItemClick}>🔑 Аутентификация</NavItem>
        <NavItem href="#pricing" active={activeSection === 'pricing'} onClick={onItemClick}>💰 Формат цен</NavItem>
        <NavItem href="#errors-global" active={activeSection === 'errors-global'} onClick={onItemClick}>⚠️ HTTP-коды</NavItem>
        <NavItem href="#error-codes" active={activeSection === 'error-codes'} onClick={onItemClick}>🔴 Коды ошибок</NavItem>

        <div className="px-4 py-2 text-xs font-bold uppercase tracking-wider text-gray-600 mt-4">
          Эндпоинты
        </div>
        <NavItem href="#ep-ping" active={activeSection === 'ep-ping'} onClick={onItemClick}><MethodBadge method="GET" /> /ping</NavItem>
        <NavItem href="#ep-balance" active={activeSection === 'ep-balance'} onClick={onItemClick}><MethodBadge method="GET" /> /v1/balance</NavItem>
        <NavItem href="#ep-prices" active={activeSection === 'ep-prices'} onClick={onItemClick}><MethodBadge method="GET" /> /v1/items/prices</NavItem>
        <NavItem href="#ep-full-prices" active={activeSection === 'ep-full-prices'} onClick={onItemClick}><MethodBadge method="GET" /> /v1/items/full-prices</NavItem>
        <NavItem href="#ep-purchases-get" active={activeSection === 'ep-purchases-get'} onClick={onItemClick}><MethodBadge method="GET" /> /v1/purchases</NavItem>
        <NavItem href="#ep-purchases-post" active={activeSection === 'ep-purchases-post'} onClick={onItemClick}><MethodBadge method="POST" /> /v1/purchases</NavItem>

        <div className="px-4 py-2 text-xs font-bold uppercase tracking-wider text-gray-600 mt-4">
          Жизненный цикл
        </div>
        <NavItem href="#lifecycle" active={activeSection === 'lifecycle'} onClick={onItemClick}>🔄 Схема переходов</NavItem>
        <NavItem href="#status-purchase" active={activeSection === 'status-purchase'} onClick={onItemClick}>📊 Статусы реквеста</NavItem>
        <NavItem href="#status-order" active={activeSection === 'status-order'} onClick={onItemClick}>📋 Статусы ордера</NavItem>

        <div className="px-4 py-2 text-xs font-bold uppercase tracking-wider text-gray-600 mt-4">
          Справочник
        </div>
        <NavItem href="#schemas" active={activeSection === 'schemas'} onClick={onItemClick}>🧬 Схемы объектов</NavItem>
      </div>

      {/* Footer */}
      <div className="px-4 py-3 border-t border-[#252d42] text-xs text-gray-600">
        xPanda P2P API © 2025
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-[#0f1117] text-[#e2e8f8] flex">
      {/* Desktop Sidebar */}
      <nav className="hidden lg:flex w-[252px] min-w-[252px] bg-[#161b27] border-r border-[#252d42] h-screen sticky top-0 flex-col shrink-0 z-50">
        <SidebarContent onItemClick={() => setMobileMenuOpen(false)} />
      </nav>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setMobileMenuOpen(false)} />
          <nav className="absolute left-0 top-0 w-[252px] h-full bg-[#161b27] flex flex-col">
            <div className="flex items-center justify-between px-4 py-3 border-b border-[#252d42]">
              <span className="font-bold">Меню</span>
              <button onClick={() => setMobileMenuOpen(false)} className="p-1 hover:bg-white/5 rounded">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto" onClick={() => setMobileMenuOpen(false)}>
              <SidebarContent />
            </div>
          </nav>
        </div>
      )}

      {/* Main Content */}
      <main className="flex-1 min-w-0 overflow-x-hidden">
        {/* Mobile Header */}
        <div className="lg:hidden flex items-center justify-between px-4 py-3 bg-[#161b27] border-b border-[#252d42]">
          <div className="flex items-center gap-2">
            <button onClick={() => setMobileMenuOpen(true)} className="p-2 hover:bg-white/5 rounded">
              <Menu className="w-5 h-5" />
            </button>
            <span className="font-bold">xPanda P2P API</span>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/partner">
              <Button size="sm" className="bg-blue-600/20 border border-blue-500/40 text-blue-400 hover:bg-blue-600/30">
                <LayoutDashboard className="w-4 h-4" />
              </Button>
            </Link>
            <Button
              onClick={() => { setAuthorizeInput(savedApiKey); setShowAuthorizeDialog(true); }}
              size="sm"
              className={savedApiKey
                ? "bg-green-600/20 border border-green-500/40 text-green-400 hover:bg-green-600/30"
                : "bg-white/5 border border-white/10 text-gray-400 hover:bg-white/10"}
            >
              {savedApiKey ? <Lock className="w-4 h-4" /> : <Unlock className="w-4 h-4" />}
            </Button>
            <Button
              onClick={() => setShowKeyDialog(true)}
              size="sm"
              className="bg-gradient-to-r from-[#ff6b00] to-[#ff8533] text-white"
            >
              <Key className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Desktop API Key Buttons */}
        <div className="hidden lg:flex fixed top-20 right-6 z-40 gap-2">
          <Link to="/partner">
            <Button className="bg-blue-600/20 border border-blue-500/40 text-blue-400 hover:bg-blue-600/30 shadow-lg">
              <LayoutDashboard className="w-4 h-4 mr-2" />
              Кабинет партнёра
            </Button>
          </Link>
          <Button
            onClick={() => { setAuthorizeInput(savedApiKey); setShowAuthorizeDialog(true); }}
            className={savedApiKey
              ? "bg-green-600/20 border border-green-500/40 text-green-400 hover:bg-green-600/30 shadow-lg"
              : "bg-white/5 border border-white/10 text-gray-300 hover:bg-white/10 shadow-lg"}
          >
            {savedApiKey ? <Lock className="w-4 h-4 mr-2" /> : <Unlock className="w-4 h-4 mr-2" />}
            {savedApiKey ? 'Authorized' : 'Authorize'}
          </Button>
          <Button
            onClick={() => setShowKeyDialog(true)}
            className="bg-gradient-to-r from-[#ff6b00] to-[#ff8533] hover:from-[#ff8533] hover:to-[#ff6b00] text-white shadow-lg"
          >
            <Key className="w-4 h-4 mr-2" />
            Получить ключ API
          </Button>
        </div>

        {/* Hero */}
        <Section id="overview">
          <div className="px-4 sm:px-8 lg:px-14 py-8 lg:py-11 border-b border-[#252d42] bg-gradient-to-b from-blue-500/5 to-transparent">
            <div className="inline-flex items-center gap-2 bg-blue-500/10 border border-blue-500/30 rounded-full px-3 py-1 text-xs text-blue-400 font-medium mb-4">
              <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
              v1 · MVP
            </div>
            <h1 className="text-3xl lg:text-4xl font-extrabold text-white tracking-tight mb-2">
              xPanda <span className="text-blue-500">P2P</span> API
            </h1>
            <p className="text-gray-400 max-w-lg leading-relaxed">
              Программный интерфейс для автоматизированной покупки игровых предметов через P2P-площадку.
            </p>
            <div className="flex flex-wrap gap-6 mt-5">
              <div className="flex items-center gap-2 text-xs text-gray-400">
                <span>🌐</span>
                <strong className="text-white">Base URL</strong>
                <code className="bg-[#070c18] px-2 py-0.5 rounded border border-[#1a2340] font-mono">https://p2p.xpanda.pro/api</code>
              </div>
              <div className="flex items-center gap-2 text-xs text-gray-400">
                <span>🔒</span>
                <strong className="text-white">Auth</strong>
                <span>Token</span>
              </div>
              <div className="flex items-center gap-2 text-xs text-gray-400">
                <span>📦</span>
                <strong className="text-white">Format</strong>
                <span>JSON</span>
              </div>
            </div>
          </div>
        </Section>

        {/* Auth */}
        <Section id="authentication">
          <TwoCol
            left={
              <>
                <div className="text-lg font-bold text-white mb-1">🔑 Аутентификация</div>
                <div className="text-sm text-gray-500 mb-3">Token-based аутентификация</div>
                <p className="text-sm text-gray-400 mb-4">
                  Для работы с интерфейсом авторизуйтесь по API ключу
                </p>
                <div className="text-xs font-bold uppercase tracking-wider text-gray-600 mb-2">Заголовок</div>
                <CodeBlock lang="http">Authorization: Token &lt;ваш_api_ключ&gt;</CodeBlock>
              </>
            }
            right={<TryItPanel title="Authentication" showModeToggle={false} />}
          />
        </Section>

        {/* Pricing */}
        <Section id="pricing">
          <TwoCol
            left={
              <>
                <div className="text-lg font-bold text-white mb-1">💰 Формат цен</div>
                <div className="text-sm text-gray-500 mb-3">Все ценовые поля в $ × 1000</div>
                <div className="p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg text-sm text-blue-400 mb-4">
                  ⚡ <strong>price_cents = price_usd × 1000</strong><br/>
                  Делите на 1000 для цены в USD.
                </div>
                <div className="text-xs font-bold uppercase tracking-wider text-gray-600 mb-2">Поля с ценами</div>
                <ParamRow name="total" type="integer" desc={<>Общий баланс в $ × 1000</>} />
                <ParamRow name="locked" type="integer" desc={<>Заблокировано под реквесты</>} />
                <ParamRow name="available" type="integer" desc={<>Доступно для новых операций</>} />
                <ParamRow name="max_price" type="integer" desc={<>Макс. цена в <code className="text-blue-400">POST /v1/purchases/</code></>} />
                <ParamRow name="exec_price" type="integer" desc="Цена исполнения ордера" />
                <ParamRow name="p" type="integer" desc="Цена в прайс-листе" />
              </>
            }
            right={
              <TryItPanel title="Конвертация цен" showModeToggle={false}>
                <div className="p-4">
                  <CodeBlock lang="таблица">{`API value   →  USD
──────────────────
1000        →  $1.00
1920        →  $1.92
10000       →  $10.00
529615      →  $529.62

price_usd = api_value / 1000`}</CodeBlock>
                </div>
              </TryItPanel>
            }
          />
        </Section>

        {/* HTTP Codes */}
        <Section id="errors-global">
          <TwoCol
            left={
              <>
                <div className="text-lg font-bold text-white mb-1">⚠️ HTTP-коды ответов</div>
                <div className="text-sm text-gray-500 mb-3">Стандартные коды статусов</div>
                <div className="space-y-2">
                  <div className="flex gap-4 py-2 border-b border-[#252d42]/50">
                    <StatusBadge code={200} />
                    <span className="text-sm text-gray-400">Успешно</span>
                  </div>
                  <div className="flex gap-4 py-2 border-b border-[#252d42]/50">
                    <StatusBadge code={400} />
                    <span className="text-sm text-gray-400">Некорректные параметры</span>
                  </div>
                  <div className="flex gap-4 py-2 border-b border-[#252d42]/50">
                    <StatusBadge code={500} />
                    <span className="text-sm text-gray-400">Внутренняя ошибка</span>
                  </div>
                  <div className="flex gap-4 py-2">
                    <span className="inline-flex text-xs font-mono font-bold px-2 py-1 rounded mb-2 bg-orange-500/20 text-orange-400">503</span>
                    <span className="text-sm text-gray-400">Сервис недоступен</span>
                  </div>
                </div>
              </>
            }
            right={<TryItPanel title="HTTP Codes" showModeToggle={false} />}
          />
        </Section>

        {/* Error Codes */}
        <Section id="error-codes">
          <TwoCol
            left={
              <>
                <div className="text-lg font-bold text-white mb-1">🔴 Коды ошибок бизнес-логики</div>
                <div className="text-sm text-gray-500 mb-3">Поле error_code в объекте реквеста при статусе Error</div>
                <table className="block overflow-x-auto whitespace-nowrap sm:whitespace-normal w-full text-sm">
                  <thead>
                    <tr className="text-left border-b border-[#252d42]">
                      <th className="pb-2 text-xs font-bold uppercase tracking-wider text-gray-600">Код</th>
                      <th className="pb-2 text-xs font-bold uppercase tracking-wider text-gray-600">Описание</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      ['trade_canceled', 'Трейд отменён'],
                      ['trade_timeout', 'Таймаут трейда'],
                      ['skins_unavailable', 'Предмет недоступен'],
                      ['trade_create_error', 'Ошибка создания трейда'],
                      ['order was cancelled by creation timeout', 'Таймаут 30 мин'],
                      ['invalid trade url', 'Некорректная трейд-ссылка'],
                      ['user_cant_trade', 'Пользователь не может торговать'],
                      ['private_inventory', 'Инвентарь закрыт'],
                      ['insufficient_funds', 'Недостаточно средств на балансе'],
                      ['client_steam_fail', 'Ошибка Steam-аккаунта покупателя, трейд не может быть отправлен. Обычно означает трейд-бан, приватный инвентарь или некорректную трейд-ссылку.'],
                      ['Purchase request timed out', 'Реквест не был исполнен за отведённое время'],
                      ['Purchase request failed', 'Реквест завершился ошибкой (обобщённая)'],
                    ].map(([code, desc]) => (
                      <tr key={code} className="border-b border-[#252d42]/50 last:border-0">
                        <td className="py-2 font-mono text-red-400 text-xs">{code}</td>
                        <td className="py-2 text-gray-400 text-sm">{desc}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            }
            right={null}
          />
        </Section>

        {/* Ping */}
        <Section id="ep-ping">
          <TwoCol
            left={
              <>
                <div className="inline-flex items-center gap-2 bg-[#1a2035] border border-[#252d42] rounded px-3 py-1.5 mb-4">
                  <MethodBadge method="GET" />
                  <code className="text-sm">/ping/</code>
                </div>
                <div className="text-lg font-bold text-white mb-1">Health Check</div>
                <div className="text-sm text-gray-500 mb-3">Проверка доступности сервиса</div>
                <p className="text-sm text-gray-400 mb-4">Не требует параметров. Используйте для мониторинга.</p>
                <div className="text-xs font-bold uppercase tracking-wider text-gray-600 mb-2">Параметры</div>
                <p className="text-sm text-gray-500 py-2">Нет параметров</p>
                <div className="text-xs font-bold uppercase tracking-wider text-gray-600 mb-2 mt-4">Ответы</div>
                <div className="flex gap-4 py-2 border-b border-[#252d42]/50">
                  <StatusBadge code={200} />
                  <span className="text-sm text-gray-400">Сервис доступен</span>
                </div>
                <div className="flex gap-4 py-2">
                  <span className="inline-flex text-xs font-mono font-bold px-2 py-1 rounded mb-2 bg-orange-500/20 text-orange-400">503</span>
                  <span className="text-sm text-gray-400">Сервис недоступен</span>
                </div>
                <div className="text-xs font-bold uppercase tracking-wider text-gray-600 mb-2 mt-4">cURL</div>
                <CodeBlock lang="shell">{`curl -X GET https://p2p.xpanda.pro/api/ping/ \\
  -H 'Authorization: Token <key>'`}</CodeBlock>
              </>
            }
            right={
              <TryItPanel 
                title="Try it — GET /ping/" 
                serverMode={serverModes.ping}
                onServerModeChange={(mode) => setServerMode('ping', mode)}
              >
                <AuthBar
                  token={tokens.ping}
                  onTokenChange={(t) => setTokens(prev => ({ ...prev, ping: t }))}
                  serverMode={serverModes.ping}
                  savedApiKey={savedApiKey}
                />
                <div className="p-4 border-b border-[#1a2340]">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-gray-600 mb-2">
                    Parameters <span className="text-gray-500 font-normal">— нет параметров</span>
                  </div>
                </div>
                <div className="p-4 border-b border-[#1a2340] bg-[#090e1a]">
                  <button
                    onClick={() => sendRequest('ping', 'GET', '/ping/')}
                    disabled={loading.ping}
                    className="flex items-center gap-2 bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded text-xs font-bold transition-colors disabled:opacity-50"
                  >
                    {loading.ping && <Loader2 className="w-3 h-3 animate-spin" />}
                    Send Request
                  </button>
                </div>
                <ResponseDisplay 
                  response={responses.ping} 
                  loading={loading.ping} 
                  onShowMockStatus={(code) => showMockStatus('ping', code)}
                  activeMockStatus={activeMockStatuses.ping}
                />
              </TryItPanel>
            }
          />
        </Section>

        {/* Balance */}
        <Section id="ep-balance">
          <TwoCol
            left={
              <>
                <div className="inline-flex items-center gap-2 bg-[#1a2035] border border-[#252d42] rounded px-3 py-1.5 mb-4">
                  <MethodBadge method="GET" />
                  <code className="text-sm">/v1/balance/</code>
                </div>
                <div className="text-lg font-bold text-white mb-1">Баланс аккаунта</div>
                <div className="text-sm text-gray-500 mb-3">Текущий баланс партнёра</div>
                <div className="flex gap-2 p-3 bg-blue-500/10 border border-blue-500/25 rounded-lg text-sm text-blue-400 mb-4">
                  <span>💡</span>
                  <span><strong>available = total − locked</strong> — доступно для новых реквестов.</span>
                </div>
                <div className="text-xs font-bold uppercase tracking-wider text-gray-600 mb-2">Поля ответа</div>
                <ParamRow name="total" type="integer" desc="Общий баланс в $ × 1000" />
                <ParamRow name="locked" type="integer" desc="Заблокировано под реквесты" />
                <ParamRow name="available" type="integer" desc="Доступно для новых операций" />
                <div className="text-xs font-bold uppercase tracking-wider text-gray-600 mb-2 mt-4">cURL</div>
                <CodeBlock lang="shell">{`curl -X GET https://p2p.xpanda.pro/api/v1/balance/ \\
  -H 'Authorization: Token <key>'`}</CodeBlock>
              </>
            }
            right={
              <TryItPanel 
                title="Try it — GET /v1/balance/" 
                serverMode={serverModes.balance}
                onServerModeChange={(mode) => setServerMode('balance', mode)}
              >
                <AuthBar
                  token={tokens.balance}
                  onTokenChange={(t) => setTokens(prev => ({ ...prev, balance: t }))}
                  serverMode={serverModes.balance}
                  savedApiKey={savedApiKey}
                />
                <div className="p-4 border-b border-[#1a2340]">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-gray-600 mb-2">
                    Parameters <span className="text-gray-500 font-normal">— нет параметров</span>
                  </div>
                </div>
                <div className="p-4 border-b border-[#1a2340] bg-[#090e1a]">
                  <button
                    onClick={() => sendRequest('balance', 'GET', '/v1/balance/')}
                    disabled={loading.balance}
                    className="flex items-center gap-2 bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded text-xs font-bold transition-colors disabled:opacity-50"
                  >
                    {loading.balance && <Loader2 className="w-3 h-3 animate-spin" />}
                    Send Request
                  </button>
                </div>
                <ResponseDisplay 
                  response={responses.balance} 
                  loading={loading.balance} 
                  onShowMockStatus={(code) => showMockStatus('balance', code)}
                  activeMockStatus={activeMockStatuses.balance}
                />
              </TryItPanel>
            }
          />
        </Section>

        {/* Prices */}
        <Section id="ep-prices">
          <TwoCol
            left={
              <>
                <div className="inline-flex items-center gap-2 bg-[#1a2035] border border-[#252d42] rounded px-3 py-1.5 mb-4">
                  <MethodBadge method="GET" />
                  <code className="text-sm">/v1/items/prices/</code>
                </div>
                <div className="text-lg font-bold text-white mb-1">Прайс-лист предметов</div>
                <div className="text-sm text-gray-500 mb-3">Цены и наличие по названиям (до 100 за запрос)</div>
                <div className="text-xs font-bold uppercase tracking-wider text-gray-600 mb-2">Query Parameters</div>
                <ParamRow name="names[]" type="string[]" desc="Список названий предметов. Без параметра — весь каталог." />
                <div className="text-xs font-bold uppercase tracking-wider text-gray-600 mb-2 mt-4">Поля ответа</div>
                <ParamRow name="timestamp" type="integer" desc="Unix timestamp обновления" />
                <ParamRow name="items[].n" type="string" desc="Название предмета" />
                <ParamRow name="items[].p" type="integer" desc="Мин. цена в $ × 1000" />
                <ParamRow name="items[].q" type="integer" desc="Кол-во доступных предметов" />
                <div className="text-xs font-bold uppercase tracking-wider text-gray-600 mb-2 mt-4">cURL</div>
                <CodeBlock lang="shell">{`curl -X GET \\
  'https://p2p.xpanda.pro/api/v1/items/prices/?names[]=<ITEM_NAME>' \\
  -H 'Authorization: Token <key>'`}</CodeBlock>
              </>
            }
            right={
              <TryItPanel 
                title="Try it — GET /v1/items/prices/" 
                serverMode={serverModes.prices}
                onServerModeChange={(mode) => setServerMode('prices', mode)}
              >
                <AuthBar
                  token={tokens.prices}
                  onTokenChange={(t) => setTokens(prev => ({ ...prev, prices: t }))}
                  serverMode={serverModes.prices}
                  savedApiKey={savedApiKey}
                />
                <div className="p-4 border-b border-[#1a2340]">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-gray-600 mb-2">Parameters</div>
                  <div className="mb-3 space-y-2">
                    <label className="text-[10px] text-gray-500 font-mono">names[] <span className="text-gray-600">(в live обязательно хотя бы 1)</span></label>
                    {pricesNamesList.map((name, i) => (
                      <div key={i} className="flex gap-1">
                        <input
                          type="text"
                          value={name}
                          onChange={(e) => setPricesNamesList(prev => prev.map((v, j) => j === i ? e.target.value : v))}
                          placeholder="AK-47 | Redline (Field-Tested)"
                          className="flex-1 bg-[#070c18] border border-[#1a2340] rounded px-2 py-1.5 text-xs text-white font-mono focus:border-blue-500/50 outline-none"
                        />
                        {pricesNamesList.length > 1 && (
                          <button onClick={() => setPricesNamesList(prev => prev.filter((_, j) => j !== i))} className="text-red-400 hover:text-red-300 px-1.5 text-xs">✕</button>
                        )}
                      </div>
                    ))}
                    <button
                      onClick={() => setPricesNamesList(prev => [...prev, ''])}
                      className="text-[10px] text-blue-400 hover:text-blue-300"
                    >+ Добавить предмет</button>
                  </div>
                </div>
                <div className="p-4 border-b border-[#1a2340] bg-[#090e1a]">
                  <button
                    onClick={() => sendRequest('prices', 'GET', '/v1/items/prices/')}
                    disabled={loading.prices || (serverModes.prices === 'live' && !pricesNamesList.some(n => n.trim()))}
                    className="flex items-center gap-2 bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded text-xs font-bold transition-colors disabled:opacity-50"
                  >
                    {loading.prices && <Loader2 className="w-3 h-3 animate-spin" />}
                    Send Request
                  </button>
                </div>
                <ResponseDisplay 
                  response={responses.prices} 
                  loading={loading.prices} 
                  onShowMockStatus={(code) => showMockStatus('prices', code)}
                  activeMockStatus={activeMockStatuses.prices}
                />
              </TryItPanel>
            }
          />
        </Section>

        {/* Full Prices — расширенный прайс премиум-партнёров */}
        <Section id="ep-full-prices">
          <TwoCol
            left={
              <>
                <div className="inline-flex items-center gap-2 bg-[#1a2035] border border-[#252d42] rounded px-3 py-1.5 mb-4">
                  <MethodBadge method="GET" />
                  <code className="text-sm">/v1/items/full-prices/</code>
                </div>
                <div className="text-lg font-bold text-white mb-1">Расширенный прайс</div>
                <div className="text-sm text-gray-500 mb-3">
                  Строка на каждый оффер: float, паттерн, фаза, наклейки, брелки, доставка
                </div>

                <div className="text-xs text-gray-400 mb-3 leading-relaxed">
                  Эндпоинт возвращает <b className="text-white">не данные, а ссылку</b> на
                  файл со сроком жизни 300 секунд. Скачивать по ней — отдельным
                  запросом, <b className="text-white">без</b> заголовка Authorization:
                  ссылка подписана в query-string, лишний заголовок ломает проверку подписи.
                </div>

                <div className="text-xs font-bold uppercase tracking-wider text-gray-600 mb-2">Response</div>
                <CodeBlock lang="json">{`{
  "download_url": "https://static.p2p.xpanda.pro/prices/price_<ts>.json?X-Amz-...",
  "expires_in_seconds": 300
}`}</CodeBlock>

                <div className="text-xs font-bold uppercase tracking-wider text-gray-600 mt-4 mb-2">
                  Структура файла
                </div>
                <CodeBlock lang="json">{`{
  "timestamp": 1785247813,
  "offers": [
    {
      "id": "467266919",            // id оффера — для закупки по offer_id
      "name": "AK-47 | Case Hardened (Field-Tested)",
      "price": 225620,              // USD-тысячные: 225620 = $225.62
      "class_id": "188530330",
      "asset_id": "52926656490",
      "float": 0.27408531308174,
      "phase": null,                // фаза Doppler
      "paint_index": 44,
      "paint_seed": 718,
      "name_tag": "The Clutch King", // именной ярлык
      "inspect_url": null,          // скоро
      "stickers": [
        {
          "slot": 0,
          "name": "Sticker | jks | Shanghai 2024",
          "wear": 0.0,              // 0.0 = целая
          "image_url": "https://cdn.steamstatic.com/..."
        }
      ],
      "keychains": []
    }
  ]
}`}</CodeBlock>

                <div className="text-xs text-gray-400 mt-3 leading-relaxed">
                  Одна строка на <code className="text-gray-300">market_hash_name</code> —
                  самый дешёвый экземпляр с его атрибутами. Поля
                  <code className="text-gray-300"> float</code>,
                  <code className="text-gray-300"> paint_seed</code>,
                  <code className="text-gray-300"> phase</code> пустые у предметов без
                  экземпляра: кейсов, наклеек, граффити.
                </div>

                <div className="text-xs font-bold uppercase tracking-wider text-gray-600 mt-4 mb-2">cURL</div>
                <CodeBlock lang="bash">{`# 1. Получить ссылку
curl -X GET 'https://p2p.xpanda.pro/api/v1/items/full-prices/' \
  -H 'Authorization: <key>'

# 2. Скачать файл — БЕЗ Authorization
curl -o prices.json '<download_url>'`}</CodeBlock>
              </>
            }
            right={
              <TryItPanel
                title="Try it — GET /v1/items/full-prices/"
                serverMode={serverModes.fullPrices}
                onServerModeChange={(mode) => setServerMode('fullPrices', mode)}
              >
                <AuthBar
                  token={tokens.fullPrices}
                  onTokenChange={(t) => setTokens(prev => ({ ...prev, fullPrices: t }))}
                  serverMode={serverModes.fullPrices}
                  savedApiKey={savedApiKey}
                />
                <div className="p-4 border-b border-[#1a2340]">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-gray-600 mb-2">
                    Parameters <span className="text-gray-500 font-normal">— нет параметров</span>
                  </div>

                </div>
                <div className="p-4 border-b border-[#1a2340] bg-[#090e1a]">
                  <button
                    onClick={() => sendRequest('fullPrices', 'GET', '/v1/items/full-prices/')}
                    disabled={loading.fullPrices}
                    className="flex items-center gap-2 bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded text-xs font-bold transition-colors disabled:opacity-50"
                  >
                    {loading.fullPrices && <Loader2 className="w-3 h-3 animate-spin" />}
                    Send Request
                  </button>
                </div>
                <ResponseDisplay
                  response={responses.fullPrices}
                  loading={loading.fullPrices}
                  onShowMockStatus={(code) => showMockStatus('fullPrices', code)}
                  activeMockStatus={activeMockStatuses.fullPrices}
                />
              </TryItPanel>
            }
          />
        </Section>

        {/* Get Purchases */}
        <Section id="ep-purchases-get">
          <TwoCol
            left={
              <>
                <div className="inline-flex items-center gap-2 bg-[#1a2035] border border-[#252d42] rounded px-3 py-1.5 mb-4">
                  <MethodBadge method="GET" />
                  <code className="text-sm">/v1/purchases/</code>
                </div>
                <div className="text-lg font-bold text-white mb-1">Статус реквеста</div>
                <div className="text-sm text-gray-500 mb-3">Получение одного или нескольких реквестов на покупку</div>
                <div className="text-xs font-bold uppercase tracking-wider text-gray-600 mb-2">Query Parameters</div>
                <ParamRow name="id" type="uuid" desc="UUID одного реквеста" />
                <ParamRow name="ids[]" type="uuid[]" desc="Список UUID (батч)" />
                <ParamRow name="custom_id" type="string" desc="Пользовательский ID" />
                <ParamRow name="custom_ids[]" type="string[]" desc="Список custom_id (батч)" />

                <div className="text-xs font-bold uppercase tracking-wider text-gray-600 mb-2 mt-4">Поля ответа</div>
                <ParamRow name="id" type="uuid" desc="Внутренний идентификатор сделки" />
                <ParamRow name="custom_id" type="string" desc="Пользовательский идентификатор" />
                <ParamRow name="product" type="string" desc="Название предмета" />
                <ParamRow name="status" type="enum" desc="Статус реквеста" />
                <ParamRow name="offer_id" type="string" desc="Идентификатор оффера. Пустая строка, если покупка шла подбором по названию." />
                <ParamRow name="max_price" type="integer" desc="Макс. цена в $ × 1000" />
                <ParamRow name="partner" type="string" desc="Идентификатор получателя из трейд-ссылки" />
                <ParamRow name="token" type="string" desc="Трейд-токен получателя" />
                <ParamRow name="created_at" type="integer" desc="Unix timestamp создания" />
                <ParamRow name="updated_at" type="integer" desc="Unix timestamp последнего изменения" />
                <ParamRow name="active_order" type="object" desc="Текущий активный ордер" />
                <ParamRow name="error_code" type="string|null" desc="Код ошибки при статусе Error" />

                <div className="text-xs font-bold uppercase tracking-wider text-gray-600 mb-2 mt-4">Пример ответа</div>
                <CodeBlock lang="json">{`{
  "id": "019fc4e9-3ef8-786a-baf3-2001fd634350",
  "custom_id": "42298052",
  "product": "M4A1-S | Emphorosaur-S (Field-Tested)",
  "status": "Error",
  "offer_id": "",
  "max_price": 484,
  "partner": "123456789",
  "token": "aBcD1234",
  "created_at": 1785715048,
  "updated_at": 1785715049,
  "active_order": {
    "status": "Error",
    "exec_price": 419,
    "steam_trade_offer_id": null,
    "steam_trade_offer_expires_at": null,
    "steam_trade_settle_at": null
  },
  "error_code": "private_inventory"
}`}</CodeBlock>

                <div className="text-xs font-bold uppercase tracking-wider text-gray-600 mb-2 mt-4">cURL</div>
                <CodeBlock lang="shell">{`curl -X GET \\
  'https://p2p.xpanda.pro/api/v1/purchases/?custom_id=<CUSTOM_ID>' \\
  -H 'Authorization: Token <key>'`}</CodeBlock>
              </>
            }
            right={
              <TryItPanel 
                title="Try it — GET /v1/purchases/" 
                serverMode={serverModes.purchasesGet}
                onServerModeChange={(mode) => setServerMode('purchasesGet', mode)}
              >
                <AuthBar
                  token={tokens.purchasesGet}
                  onTokenChange={(t) => setTokens(prev => ({ ...prev, purchasesGet: t }))}
                  serverMode={serverModes.purchasesGet}
                  savedApiKey={savedApiKey}
                />
                <div className="p-4 border-b border-[#1a2340]">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-gray-600 mb-2">Parameters</div>
                  <div className="mb-3 space-y-2">
                    <label className="text-[10px] text-gray-500 font-mono">id / ids[] <span className="text-gray-600">(UUID реквестов)</span></label>
                    {purchasesIdsList.map((val, i) => (
                      <div key={`id-${i}`} className="flex gap-1">
                        <input
                          type="text"
                          value={val}
                          onChange={(e) => setPurchasesIdsList(prev => prev.map((v, j) => j === i ? e.target.value : v))}
                          placeholder="550e8400-e29b-41d4-..."
                          className="flex-1 bg-[#070c18] border border-[#1a2340] rounded px-2 py-1.5 text-xs text-white font-mono focus:border-blue-500/50 outline-none"
                        />
                        {purchasesIdsList.length > 1 && (
                          <button onClick={() => setPurchasesIdsList(prev => prev.filter((_, j) => j !== i))} className="text-red-400 hover:text-red-300 px-1.5 text-xs">✕</button>
                        )}
                      </div>
                    ))}
                    <button onClick={() => setPurchasesIdsList(prev => [...prev, ''])} className="text-[10px] text-blue-400 hover:text-blue-300">+ Добавить ID</button>
                  </div>
                  <div className="mb-3 space-y-2">
                    <label className="text-[10px] text-gray-500 font-mono">custom_id / custom_ids[] <span className="text-gray-600">(ваш ID)</span></label>
                    {purchasesCustomIdsList.map((val, i) => (
                      <div key={`cid-${i}`} className="flex gap-1">
                        <input
                          type="text"
                          value={val}
                          onChange={(e) => setPurchasesCustomIdsList(prev => prev.map((v, j) => j === i ? e.target.value : v))}
                          placeholder="my_order_123"
                          className="flex-1 bg-[#070c18] border border-[#1a2340] rounded px-2 py-1.5 text-xs text-white font-mono focus:border-blue-500/50 outline-none"
                        />
                        {purchasesCustomIdsList.length > 1 && (
                          <button onClick={() => setPurchasesCustomIdsList(prev => prev.filter((_, j) => j !== i))} className="text-red-400 hover:text-red-300 px-1.5 text-xs">✕</button>
                        )}
                      </div>
                    ))}
                    <button onClick={() => setPurchasesCustomIdsList(prev => [...prev, ''])} className="text-[10px] text-blue-400 hover:text-blue-300">+ Добавить custom_id</button>
                  </div>
                </div>
                <div className="p-4 border-b border-[#1a2340] bg-[#090e1a]">
                  <button
                    onClick={() => sendRequest('purchasesGet', 'GET', '/v1/purchases/')}
                    disabled={loading.purchasesGet || (serverModes.purchasesGet === 'live' && !purchasesIdsList.some(n => n.trim()) && !purchasesCustomIdsList.some(n => n.trim()))}
                    className="flex items-center gap-2 bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded text-xs font-bold transition-colors disabled:opacity-50"
                  >
                    {loading.purchasesGet && <Loader2 className="w-3 h-3 animate-spin" />}
                    Send Request
                  </button>
                </div>
                <ResponseDisplay 
                  response={responses.purchasesGet} 
                  loading={loading.purchasesGet} 
                  onShowMockStatus={(code) => showMockStatus('purchasesGet', code)}
                  activeMockStatus={activeMockStatuses.purchasesGet}
                />
              </TryItPanel>
            }
          />
        </Section>

        {/* Post Purchases */}
        <Section id="ep-purchases-post">
          <TwoCol
            left={
              <>
                <div className="inline-flex items-center gap-2 bg-[#1a2035] border border-[#252d42] rounded px-3 py-1.5 mb-4">
                  <MethodBadge method="POST" />
                  <code className="text-sm">/v1/purchases/</code>
                </div>
                <div className="text-lg font-bold text-white mb-1">Создать реквест</div>
                <div className="text-sm text-gray-500 mb-3">Создание заявки на покупку предмета</div>
                <p className="text-sm text-gray-400 mb-4">
                  Система блокирует <code className="text-blue-400">max_price</code> на балансе и начинает поиск товара. Статус сразу — <code className="text-blue-400">MONEY_BLOCKED</code>.
                </p>
                <div className="text-xs font-bold uppercase tracking-wider text-gray-600 mb-2">Request Body</div>
                <ParamRow name="product" type="string" required desc="Точное название предмета из Steam Market" />
                <ParamRow name="partner" type="string" required desc="Steam ID получателя (64-bit)" />
                <ParamRow name="token" type="string" required desc="Трейд-токен получателя из Steam" />
                <ParamRow name="max_price" type="integer" required desc="Макс. цена в $ × 1000 (пример: 10000 = $10)" />
                <ParamRow name="offer_id" type="string" desc="НЕОБЯЗАТЕЛЬНО. id оффера из /v1/items/full-prices/ — закупается именно этот экземпляр. Пусто — подбор по названию и потолку цены. product и max_price обязательны в обоих случаях." />
                <ParamRow name="custom_id" type="string" required desc="Уникальный ID в вашей системе" />
                <ParamRow name="sign" type="string" required desc="Подпись запроса (HMAC-SHA256)" />
                <div className="text-xs font-bold uppercase tracking-wider text-gray-600 mb-2 mt-4">cURL</div>
                <CodeBlock lang="shell">{`curl -X POST https://p2p.xpanda.pro/api/v1/purchases/ \\
  -H 'Content-Type: application/json' \\
  -H 'Authorization: Token <key>' \\
  -d '{
    "product": "<ITEM_NAME>",
    "partner": "<STEAM_ID>",
    "token": "<TRADE_TOKEN>",
    "max_price": <MAX_PRICE>,
    "custom_id": "<CUSTOM_ID>",
    "sign": "<SIGN>"
  }'`}</CodeBlock>
              </>
            }
            right={
              <TryItPanel
                title="Try it — POST /v1/purchases/"
                showModeToggle={false}
              >
                <div className="p-4 border-b border-[#1a2340]">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-gray-600 mb-2">Request Body</div>
                  <div className="space-y-3">
                    <div>
                      <label className="text-[10px] text-gray-500 font-mono">product <span className="text-red-400">*required</span></label>
                      <input
                        type="text"
                        value={postPurchaseData.product}
                        onChange={(e) => setPostPurchaseData(prev => ({ ...prev, product: e.target.value }))}
                        placeholder="AK-47 | Redline (Field-Tested)"
                        className="w-full mt-1 bg-[#070c18] border border-[#1a2340] rounded px-2 py-1.5 text-xs text-white font-mono focus:border-blue-500/50 outline-none"
                      />
                    </div>
                    <div>
                      <label className="text-[10px] text-gray-500 font-mono">partner <span className="text-red-400">*required</span></label>
                      <input
                        type="text"
                        value={postPurchaseData.partner}
                        onChange={(e) => setPostPurchaseData(prev => ({ ...prev, partner: e.target.value }))}
                        placeholder="76561198000000000"
                        className="w-full mt-1 bg-[#070c18] border border-[#1a2340] rounded px-2 py-1.5 text-xs text-white font-mono focus:border-blue-500/50 outline-none"
                      />
                    </div>
                    <div>
                      <label className="text-[10px] text-gray-500 font-mono">token <span className="text-red-400">*required</span></label>
                      <input
                        type="text"
                        value={postPurchaseData.token}
                        onChange={(e) => setPostPurchaseData(prev => ({ ...prev, token: e.target.value }))}
                        placeholder="trade_token"
                        className="w-full mt-1 bg-[#070c18] border border-[#1a2340] rounded px-2 py-1.5 text-xs text-white font-mono focus:border-blue-500/50 outline-none"
                      />
                    </div>
                    <div>
                      <label className="text-[10px] text-gray-500 font-mono">max_price <span className="text-red-400">*required</span></label>
                      <input
                        type="text"
                        value={postPurchaseData.max_price}
                        onChange={(e) => setPostPurchaseData(prev => ({ ...prev, max_price: e.target.value }))}
                        placeholder="10000"
                        className="w-full mt-1 bg-[#070c18] border border-[#1a2340] rounded px-2 py-1.5 text-xs text-white font-mono focus:border-blue-500/50 outline-none"
                      />
                    </div>
                    <div>
                      <label className="text-[10px] text-gray-500 font-mono">custom_id <span className="text-red-400">*required</span></label>
                      <input
                        type="text"
                        value={postPurchaseData.custom_id}
                        onChange={(e) => setPostPurchaseData(prev => ({ ...prev, custom_id: e.target.value }))}
                        placeholder="my_order_123"
                        className="w-full mt-1 bg-[#070c18] border border-[#1a2340] rounded px-2 py-1.5 text-xs text-white font-mono focus:border-blue-500/50 outline-none"
                      />
                    </div>
                    <div>
                      <label className="text-[10px] text-gray-500 font-mono">sign <span className="text-red-400">*required</span></label>
                      <input
                        type="text"
                        value={postPurchaseData.sign}
                        onChange={(e) => setPostPurchaseData(prev => ({ ...prev, sign: e.target.value }))}
                        placeholder="HMAC-SHA256 signature"
                        className="w-full mt-1 bg-[#070c18] border border-[#1a2340] rounded px-2 py-1.5 text-xs text-white font-mono focus:border-blue-500/50 outline-none"
                      />
                    </div>
                  </div>
                </div>
                <div className="p-4 border-b border-[#1a2340] bg-[#090e1a]">
                  <button
                    onClick={() => sendRequest('purchasesPost', 'POST', '/v1/purchases/', {
                      product: postPurchaseData.product,
                      partner: postPurchaseData.partner,
                      token: postPurchaseData.token,
                      max_price: parseInt(postPurchaseData.max_price) || 0,
                      custom_id: postPurchaseData.custom_id,
                      sign: postPurchaseData.sign,
                    })}
                    disabled={loading.purchasesPost}
                    className="flex items-center gap-2 bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded text-xs font-bold transition-colors disabled:opacity-50"
                  >
                    {loading.purchasesPost && <Loader2 className="w-3 h-3 animate-spin" />}
                    Send Request
                  </button>
                </div>
                <ResponseDisplay 
                  response={responses.purchasesPost} 
                  loading={loading.purchasesPost} 
                  onShowMockStatus={(code) => showMockStatus('purchasesPost', code)}
                  activeMockStatus={activeMockStatuses.purchasesPost}
                />
              </TryItPanel>
            }
          />
        </Section>

        {/* Lifecycle */}
        <Section id="lifecycle">
          <TwoCol
            left={
              <>
                <div className="text-lg font-bold text-white mb-1">🔄 Схема переходов</div>
                <div className="text-sm text-gray-500 mb-4">Полный граф статусов реквеста и ордера</div>
                
                <div className="text-xs font-bold uppercase tracking-wider text-gray-600 mb-2">▲ Реквест (Purchase)</div>
                <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-600 mb-1">Успешный путь</p>
                <div className="flex items-center gap-1 text-xs flex-wrap mb-3">
                  <span className="px-2 py-1 rounded border border-blue-500 text-blue-400 font-mono">MONEY_BLOCKED</span>
                  <span className="text-gray-600">→</span>
                  <span className="px-2 py-1 rounded border border-yellow-500 text-yellow-400 font-mono">IN_PROGRESS</span>
                  <span className="text-gray-600">→</span>
                  <span className="px-2 py-1 rounded border border-green-500 text-green-400 font-mono">DONE ✓</span>
                </div>
                
                <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-600 mb-1">Ошибка → ретрай</p>
                <div className="flex items-center gap-1 text-xs flex-wrap mb-3">
                  <span className="px-2 py-1 rounded border border-yellow-500 text-yellow-400 font-mono">IN_PROGRESS</span>
                  <span className="text-gray-600">→</span>
                  <span className="px-2 py-1 rounded border border-red-500 text-red-400 font-mono">ERROR</span>
                  <span className="text-gray-600">→</span>
                  <span className="px-2 py-1 rounded border border-blue-500 text-blue-400 font-mono">MONEY_BLOCKED</span>
                </div>

                <div className="text-xs font-bold uppercase tracking-wider text-gray-600 mb-2 mt-4">▼ Ордер (active_order)</div>
                <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-600 mb-1">Успешный путь</p>
                <div className="flex items-center gap-1 text-xs flex-wrap">
                  <span className="px-2 py-1 rounded border border-blue-500 text-blue-400 font-mono">PENDING</span>
                  <span className="text-gray-600">→</span>
                  <span className="px-2 py-1 rounded border border-yellow-500 text-yellow-400 font-mono">IN_PROGRESS</span>
                  <span className="text-gray-600">→</span>
                  <span className="px-2 py-1 rounded border border-green-500 text-green-400 font-mono">BOUGHT</span>
                  <span className="text-gray-600">→</span>
                  <span className="px-2 py-1 rounded border border-purple-500 text-purple-400 font-mono">WFT</span>
                  <span className="text-gray-600">→</span>
                  <span className="px-2 py-1 rounded border border-blue-400 text-blue-300 font-mono">TRADE</span>
                  <span className="text-gray-600">→</span>
                  <span className="px-2 py-1 rounded border border-green-500 text-green-400 font-mono">DONE ✓</span>
                </div>
              </>
            }
            right={
              <TryItPanel title="State transitions" showModeToggle={false}>
                <div className="p-4 space-y-4">
                  <div>
                    <div className="text-[10px] font-bold uppercase tracking-wider text-gray-600 mb-2">Purchase</div>
                    <CodeBlock lang="enum">{`MONEY_BLOCKED
  ├─► IN_PROGRESS   (товар найден)
  └─► ERROR         (30 мин, не найден)
IN_PROGRESS
  ├─► DONE
  ├─► IN_PROGRESS   (новый ордер)
  └─► ERROR (таймаут) ─► MONEY_BLOCKED
DONE ─► ERROR (Trade Protection)`}</CodeBlock>
                  </div>
                  <div>
                    <div className="text-[10px] font-bold uppercase tracking-wider text-gray-600 mb-2">Order</div>
                    <CodeBlock lang="enum">{`PENDING → IN_PROGRESS → BOUGHT
                     → WAITING_FOR_TRADE
                         → TRADE
                           ├─► DONE ✓
                           └─► ERROR
DONE → RETURNED → Purchase ERROR`}</CodeBlock>
                  </div>
                </div>
              </TryItPanel>
            }
          />
        </Section>

        {/* Status Purchase */}
        <Section id="status-purchase">
          <div className="px-8 lg:px-14 py-7 border-b border-[#252d42]">
            <h2 className="text-lg font-bold text-white mb-1">📊 Статусная модель реквеста</h2>
            <p className="text-sm text-gray-500">Жизненный цикл Purchase</p>
          </div>
          <TwoCol
            left={
              <div className="space-y-3 pt-2">
                {[
                  { code: 'MONEY_BLOCKED', color: 'blue', desc: 'Средства заблокированы. Система ищет товар.', note: '→ IN_PROGRESS / → ERROR (30 мин)' },
                  { code: 'IN_PROGRESS', color: 'yellow', desc: 'Товар найден, работаем с площадкой.', note: '→ DONE | → IN_PROGRESS (новый ордер) | → ERROR' },
                  { code: 'ERROR', color: 'red', desc: 'Ошибка. Средства разблокированы.', note: '→ MONEY_BLOCKED (ретрай) | из DONE (Trade Protection)' },
                  { code: 'DONE', color: 'green', desc: 'Покупка выполнена.', note: '→ ERROR если Trade Protection (7 дней)', badge: 'final' },
                ].map((item) => (
                  <div key={item.code} className="flex flex-col sm:flex-row border border-[#252d42] rounded-lg overflow-hidden">
                    <div className={`w-full sm:w-44 shrink-0 p-3 font-mono text-xs font-bold border-b sm:border-b-0 sm:border-r border-[#252d42] bg-${item.color}-500/10 text-${item.color}-400`}>
                      {item.code}
                      {item.badge && (
                        <span className={`block mt-1 text-[10px] px-1 py-0.5 rounded bg-${item.color}-500/20 w-fit`}>{item.badge}</span>
                      )}
                    </div>
                    <div className="flex-1 p-3 text-sm text-gray-400">
                      {item.desc}
                      <small className="block mt-1 text-gray-600">{item.note}</small>
                    </div>
                  </div>
                ))}
              </div>
            }
            right={
              <div className="p-4">
                <CodeBlock lang="enum">{`MONEY_BLOCKED   // начальный
IN_PROGRESS
ERROR           // + ретрай
DONE            // конечный`}</CodeBlock>
              </div>
            }
          />
        </Section>

        {/* Status Order */}
        <Section id="status-order">
          <div className="px-8 lg:px-14 py-7 border-b border-[#252d42]">
            <h2 className="text-lg font-bold text-white mb-1">📋 Статусная модель ордера</h2>
            <p className="text-sm text-gray-500">Поле active_order внутри Purchase</p>
          </div>
          <TwoCol
            left={
              <div className="space-y-3 pt-2">
                {[
                  { code: 'PENDING', color: 'blue', desc: 'Ордер создан, ожидает обработки.' },
                  { code: 'IN_PROGRESS', color: 'yellow', desc: 'Запрос отправлен на площадку.' },
                  { code: 'BOUGHT', color: 'green', desc: 'Деньги оплачены, создаётся трейд.' },
                  { code: 'WAITING_FOR_TRADE', color: 'purple', desc: 'Ожидается создание трейда в Steam.' },
                  { code: 'TRADE', color: 'blue', desc: 'Trade Offer создан.', note: '→ DONE / → ERROR (trade_timeout)' },
                  { code: 'ERROR', color: 'red', desc: 'Ошибка. Реквест создаст новый ордер.', note: 'Причина в error_code' },
                  { code: 'DONE', color: 'green', desc: 'Обмен выполнен.', badge: 'final' },
                  { code: 'RETURNED', color: 'orange', desc: 'Trade Protection. Реквест → ERROR.' },
                ].map((item) => (
                  <div key={item.code} className="flex flex-col sm:flex-row border border-[#252d42] rounded-lg overflow-hidden">
                    <div className={`w-full sm:w-44 shrink-0 p-3 font-mono text-xs font-bold border-b sm:border-b-0 sm:border-r border-[#252d42] bg-${item.color}-500/10 text-${item.color}-400`}>
                      {item.code}
                      {item.badge && (
                        <span className={`block mt-1 text-[10px] px-1 py-0.5 rounded bg-${item.color}-500/20 w-fit`}>{item.badge}</span>
                      )}
                    </div>
                    <div className="flex-1 p-3 text-sm text-gray-400">
                      {item.desc}
                      {item.note && <small className="block mt-1 text-gray-600">{item.note}</small>}
                    </div>
                  </div>
                ))}
              </div>
            }
            right={
              <div className="p-4">
                <CodeBlock lang="enum">{`PENDING
IN_PROGRESS
BOUGHT
WAITING_FOR_TRADE
TRADE
ERROR
DONE            // final
RETURNED`}</CodeBlock>
              </div>
            }
          />
        </Section>

        {/* Schemas */}
        <Section id="schemas">
          <div className="px-8 lg:px-14 py-7 border-b border-[#252d42]">
            <h2 className="text-lg font-bold text-white mb-1">🧬 Схемы объектов</h2>
            <p className="text-sm text-gray-500">Структуры данных API</p>
          </div>
          <TwoCol
            left={
              <div className="pt-2">
                <p className="font-bold text-white mb-3 text-sm">Purchase</p>
                <table className="w-full text-sm mb-6">
                  <thead>
                    <tr className="text-left border-b border-[#252d42]">
                      <th className="pb-2 text-[10px] font-bold uppercase tracking-wider text-gray-600">Поле</th>
                      <th className="pb-2 text-[10px] font-bold uppercase tracking-wider text-gray-600">Тип</th>
                      <th className="pb-2 text-[10px] font-bold uppercase tracking-wider text-gray-600">Описание</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      ['id', 'uuid', 'Внутренний UUID реквеста'],
                      ['custom_id', 'string', 'Пользовательский ID'],
                      ['status', 'enum', 'MONEY_BLOCKED, IN_PROGRESS, ERROR, DONE'],
                      ['product', 'string', 'Название предмета'],
                      ['max_price', 'integer', 'Макс. цена ($ × 1000)'],
                      ['active_order', 'object|null', 'Текущий ордер'],
                    ].map(([name, type, desc]) => (
                      <tr key={name} className="border-b border-[#252d42]/50">
                        <td className="py-2 font-mono text-blue-400 text-xs">{name}</td>
                        <td className="py-2 font-mono text-purple-400 text-xs">{type}</td>
                        <td className="py-2 text-gray-400 text-xs">{desc}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                <p className="font-bold text-white mb-3 text-sm">Order (active_order)</p>
                <table className="block overflow-x-auto whitespace-nowrap sm:whitespace-normal w-full text-sm">
                  <thead>
                    <tr className="text-left border-b border-[#252d42]">
                      <th className="pb-2 text-[10px] font-bold uppercase tracking-wider text-gray-600">Поле</th>
                      <th className="pb-2 text-[10px] font-bold uppercase tracking-wider text-gray-600">Тип</th>
                      <th className="pb-2 text-[10px] font-bold uppercase tracking-wider text-gray-600">Описание</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      ['id', 'uuid', 'UUID ордера'],
                      ['status', 'enum', 'Статус ордера'],
                      ['exec_price', 'integer|null', 'Цена исполнения ($ × 1000)'],
                      ['error_code', 'string|null', 'Код ошибки при ERROR'],
                    ].map(([name, type, desc]) => (
                      <tr key={name} className="border-b border-[#252d42]/50">
                        <td className="py-2 font-mono text-blue-400 text-xs">{name}</td>
                        <td className="py-2 font-mono text-purple-400 text-xs">{type}</td>
                        <td className="py-2 text-gray-400 text-xs">{desc}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            }
            right={
              <div className="p-4">
                <CodeBlock lang="json">{`{
  "id": "550e8400-e29b-41d4-a716-446655440000",
  "custom_id": "my_order_123",
  "status": "IN_PROGRESS",
  "product": "AK-47 | Redline (Field-Tested)",
  "max_price": 15000,
  "active_order": {
    "id": "ord-uuid-here",
    "status": "BOUGHT",
    "exec_price": 14850,
    "error_code": null
  }
}`}</CodeBlock>
              </div>
            }
          />
        </Section>

        {/* Footer spacer */}
        <div className="h-20" />
      </main>

      {/* Get API Key Dialog */}
      {/* Authorize dialog */}
      <Dialog open={showAuthorizeDialog} onOpenChange={(open) => { setShowAuthorizeDialog(open); if (!open) setAuthorizeInput(savedApiKey); }}>
        <DialogContent className="bg-[#161b22] border-[#30363d] text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              {savedApiKey
                ? <Lock className="w-5 h-5 text-green-400" />
                : <Unlock className="w-5 h-5 text-gray-400" />}
              Authorize
            </DialogTitle>
            <DialogDescription className="text-[#8b949e]">
              Введите API ключ для выполнения live-запросов. Ключ будет сохранён в браузере.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <label className="text-xs font-bold uppercase tracking-wider text-gray-500">
                API Key (без префикса Token)
              </label>
              <input
                type="password"
                value={authorizeInput}
                onChange={(e) => setAuthorizeInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && authorizeInput.trim()) saveApiKey(authorizeInput); }}
                placeholder="Вставьте API ключ..."
                className="w-full bg-[#0d1117] border border-[#30363d] rounded-lg px-3 py-2 text-sm text-white font-mono outline-none focus:border-blue-500/60 transition-colors"
                autoFocus
              />
            </div>

            {savedApiKey && (
              <div className="flex items-center gap-2 p-2 bg-green-500/10 border border-green-500/25 rounded-lg text-xs text-green-400">
                <Lock className="w-3.5 h-3.5 shrink-0" />
                <span>Ключ сохранён и применён ко всем запросам</span>
              </div>
            )}

            <div className="flex gap-2 pt-1">
              <Button
                onClick={() => saveApiKey(authorizeInput)}
                disabled={!authorizeInput.trim()}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-40"
              >
                <Lock className="w-4 h-4 mr-2" />
                Authorize
              </Button>
              {savedApiKey && (
                <Button
                  onClick={clearApiKey}
                  className="bg-red-600/20 border border-red-500/30 text-red-400 hover:bg-red-600/30"
                >
                  Logout
                </Button>
              )}
              <Button
                onClick={() => { setShowAuthorizeDialog(false); setAuthorizeInput(savedApiKey); }}
                className="bg-white/5 border border-white/10 text-gray-300 hover:bg-white/10"
              >
                Close
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showKeyDialog} onOpenChange={setShowKeyDialog}>
        <DialogContent className="bg-[#161b22] border-[#30363d] text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <Key className="w-5 h-5 text-[#ff6b00]" />
              Получить API ключ
            </DialogTitle>
            <DialogDescription className="text-[#8b949e]">
              Для получения API ключа свяжитесь с нами
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div className="bg-[#0d1117] rounded-lg p-4 border border-[#30363d]">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-[#1a2332] rounded-lg flex items-center justify-center">
                  <MessageCircle className="w-5 h-5 text-[#ff6b00]" />
                </div>
                <div>
                  <p className="text-white font-medium">Telegram</p>
                  <p className="text-[#8b949e] text-sm">Быстрый ответ</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <code className="flex-1 bg-[#161b22] px-3 py-2 rounded text-sm text-[#c9d1d9]">
                  @xpanda_support
                </code>
                <button
                  onClick={() => handleCopy('@xpanda_support')}
                  className="p-2 hover:bg-[#30363d] rounded transition-colors"
                >
                  {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4 text-[#8b949e]" />}
                </button>
              </div>
            </div>

            <div className="bg-[#0d1117] rounded-lg p-4 border border-[#30363d]">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-[#1a2332] rounded-lg flex items-center justify-center">
                  <span className="text-[#ff6b00] text-lg">✉</span>
                </div>
                <div>
                  <p className="text-white font-medium">Email</p>
                  <p className="text-[#8b949e] text-sm">Для корпоративных запросов</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <code className="flex-1 bg-[#161b22] px-3 py-2 rounded text-sm text-[#c9d1d9]">
                  site@xpanda.team
                </code>
                <button
                  onClick={() => handleCopy('site@xpanda.team')}
                  className="p-2 hover:bg-[#30363d] rounded transition-colors"
                >
                  <Copy className="w-4 h-4 text-[#8b949e]" />
                </button>
              </div>
            </div>

            <div className="text-[#8b949e] text-sm space-y-2">
              <p>📋 <strong>Что нужно указать в запросе:</strong></p>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>Ваш Steam ID</li>
                <li>Предполагаемый объём торгов</li>
                <li>Название проекта/сервиса</li>
              </ul>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
